import React, {
  useContext, useEffect, useState
}                                     from 'react'
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Stack,
  Typography,
}                                     from '@mui/material'
import { Trans, useTranslation }      from 'react-i18next'
import { useNavigate, useParams }     from 'react-router-dom'
import { useApi }                     from '../utils/api'
import { useSnackbar }                from 'notistack'
import AlertSimpleDialog              from '../ui/components/alert-simple-dialog'
import Page                           from '../ui/components/Page'
import { BaseBreadcrumbs }            from '../ui/components/BaseBreadcrumbs'
import ShortFormControl               from '../ui/components/short-form-control'
import ShortFormSelect                from '../ui/components/short-form-select'
import Consts                         from '../Consts'
import ScheduleChangeDialog           from './ScheduleChangeDialog'
import SummaryChangesDialog           from './SummaryChangesDialog'
import { fDateTime }                  from "../utils/textutils"
import ScheduledChangeDeleteDialog    from "./ScheduledChangeDeleteDialog"
import BaseTable                      from "../ui/components/base-table"
import { AppContext }                 from '../App'

export default function ServiceOperatorPage() {

  const api = useApi()
  const { user } = useContext(AppContext)
  const navigate = useNavigate()
  const { serviceId, serviceOperatorId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [operators, setOperators] = useState([])
  const [service, setService] = useState(null)
  const [values, setValues] = useState({
    pattern: '',
    keyword: '',
    operatorId: '',
    priceWithVat: '',
    vatRate: '0.23',
    chargeType: 'single',
    chargeDuration: '',
    firstChargeAttempts: '',
    otherChargeAttempts: '',
    active: false,
    reminderTime: '',
    chargeIncorrectAnswer: false,
    priceWithVatIncorrectAnswer: '',
  })
  const [shownDeleteDialog, setShownDeleteDialog] = useState(false)
  const [forceReloadChangesCounter, setForceReloadChangesCounter] = useState(0)

  useEffect(() => {
    api.operator.list().then(({ data }) => setOperators(data)).catch(e => console.error(e))
  }, [])

  useEffect(() => {
    if (!isNaN(serviceId)) {
      api.service.detail(serviceId).then(data => setService(data)).catch(e => console.error(e))
    }
  }, [serviceId])

  useEffect(() => {
    (async () => {
      if (!isNaN(serviceOperatorId)) {
        try{
          let serviceOperator = await api.serviceOperator.detail(serviceOperatorId)
          setValues({
            ...serviceOperator
          })
        } catch (e){
          enqueueSnackbar(t('Can\'t load service operator'), {
            variant: "error"
          })
        }
      }
    })()
  }, [serviceOperatorId])

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value })
  }

  const handleChangeDirect = (prop, value) => {
    setValues({ ...values, [prop]: value })
  }

  const handleCloseDeleteDialog = (result) => {
    setShownDeleteDialog(false)
    if (result) {
      api.serviceOperator.delete(serviceOperatorId).then(() => {
        enqueueSnackbar(t('Service operator deleted'), {
          variant: 'success'
        })
        navigate(`/services/${serviceId}`)
      }).catch(error => {
        enqueueSnackbar(t('Failed to delete service operator'), {
          variant: 'error'
        })
      })
    }
  }

  const prepareFormData = () => {
    return {
      ...values,
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!values.pattern) {
      enqueueSnackbar(t('Enter pattern'), {
        variant: 'warning'
      })
      return
    }
    let data = { ...values,
      serviceId: serviceId,
    }
    if (!data.chargeIncorrectAnswer) {
      data.priceWithVatIncorrectAnswer = null
    }
    try {
      let sOperator = await api.serviceOperator.save(data)
      navigate(`/services/${serviceId}/operator/${sOperator.id}`)
      enqueueSnackbar(t('Service operator saved'), {
        variant: 'success'
      })
    } catch (e) {
      enqueueSnackbar(t('Failed to save service operator'), {
        variant: 'error'
      })
    }
  }

  const ScheduledChanges = ({ serviceOperatorId, setForceReloadChangesCounter, forceReloadChangesCounter, t }) => {

    const api = useApi()

    const changesColumns = [
      {
        field: 'scheduled', headerName: t('Scheduled'), flex: 1, sortable: false, filterable: false,
        renderCell: (cell) => {
          return cell.row.scheduled ? fDateTime(cell.row.scheduled) : '---'
        }
      },
      {
        field: 'executed', headerName: t('Executed'), flex: 1, sortable: false, filterable: false,
        renderCell: (cell) => {
          return cell.row.executed ? fDateTime(cell.row.executed) : '---'
        }
      },
      { field: 'id', headerName: '#', flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
          return <>
            <SummaryChangesDialog object={cell.row?.originalData} changedObject={cell.row?.changedData} />
            {!cell.row.executed && <ScheduledChangeDeleteDialog change={cell.row} onResult={(res) => {
              if (res) {
                setForceReloadChangesCounter((prev) => prev + 1)
              }}} />}
          </>
        }},
    ]

    return <React.Fragment>
      <Stack direction="row" alignItems="center" mb={2} spacing={2}>
        <Typography variant="h4" gutterBottom>{t('Scheduled changes')}</Typography>
      </Stack>
      <BaseTable
          columns={changesColumns}
          query={null}
          forceReloadCounter={forceReloadChangesCounter}
          loadCallback={async (query, page, pageSize) => {
            try {
              return await api.scheduleChange.serviceOperator.list(serviceOperatorId)
            } catch (e) {
              console.error('Failed to load scheduled changes for serviceOperator', e)
              return {
                data: [],
                totalCount: 0,
              }
            }
          }}
      />
    </React.Fragment>
  }

  return (
    <Page title={t('Service operator')}>
      <Container component="main">
        <BaseBreadcrumbs key={'breadcrumb-service-operator'}
                         title={values?.name ?? t('Service operator')}
                         items={[
                           { title: t('Services'), href: `/services` },
                           { title: service?.title ?? t('Service'), href: `/services/${serviceId}` }
                         ]}/>
        <Stack direction={'row'} spacing={2}>
          <Typography variant={'h6'} pl={2}>{service?.title}</Typography>
        </Stack>
        <Grid component={'form'} onSubmit={onSubmit} item xs={12}>
          <Paper sx={{ p: 2, width: '100%' }}>
            <Grid container sx={{ width: '100%' }} spacing={2}>
              <Grid item xs={12} md={4}>
                <ShortFormControl name={'pattern'} title={t('Pattern')} value={values.pattern} handleChange={handleChange}/>
                <Stack direction={'row'} justifyContent={'end'}>
                  <Typography variant={'caption'}>{t(`helper-serviceOperatorPattern`)}</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <ShortFormControl name={'keyword'} title={t('Normalized keyword')} value={values.keyword} handleChange={handleChange}/>
              </Grid>
              <Grid item xs={12} md={4}>
                {operators && <ShortFormSelect name={'operatorId'} title={t('Operator')} value={values.operatorId} handleChange={handleChange} allowAllOption={false} items={operators} />}
              </Grid>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel control={<Checkbox/>} checked={values.active} onChange={(e) => {
                    handleChangeDirect('active', e.target.checked)
                  }} label={t('Active')}/>
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <ShortFormControl name={'priceWithVat'} title={t('Price')} value={values.priceWithVat} type={'number'} handleChange={handleChange}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <ShortFormControl name={'vatRate'} title={t('Vat rate')} value={values.vatRate} type={'number'} inputProps={{ step: '0.01' }} handleChange={handleChange}/>
              </Grid>
              {[Consts.Service.typePayment, Consts.Service.typeDigitalAccess].includes(service?.type) && <>
                <Grid item xs={12} md={6}>
                  <ShortFormControl name={'chargeDuration'} title={t('Duration (days)')} value={values.chargeDuration} type={'number'} handleChange={handleChange}/>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ShortFormSelect name={'chargeType'} title={t('Type')} value={values.chargeType} handleChange={handleChange} allowAllOption={false} items={[
                    { id: 'single', name: t('chargeTypeSingle') },
                    { id: 'recurring', name: t('chargeTypeRecurring') },
                  ]} />
                </Grid>
                {values?.chargeType === 'recurring' && <>
                  <Grid item xs={12} md={6}>
                    <ShortFormControl name={'firstChargeAttempts'} title={t('First charge (attempts)')} value={values.firstChargeAttempts} type={'number'} handleChange={handleChange}/>
                    <Stack direction={'row'} justifyContent={'flex-end'}>
                      <Typography variant={'caption'}>{t(`attempts-once-per-day`)}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ShortFormControl name={'otherChargeAttempts'} title={t('Other charges (attempts)')} value={values.otherChargeAttempts} type={'number'} handleChange={handleChange}/>
                    <Stack direction={'row'} justifyContent={'flex-end'}>
                      <Typography variant={'caption'}>{t(`attempts-once-per-week`)}</Typography>
                    </Stack>
                  </Grid>
                </>}
                <Grid item xs={12} md={6}>
                  <ShortFormControl name={'reminderTime'} title={t('Reminder time')} value={values.reminderTime} type={'number'} handleChange={handleChange}/>
                </Grid>
                </>}
              {service?.type === Consts.Service.typeCrossword && <>
                <Grid item xs={12}>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      checked={values.chargeIncorrectAnswer}
                      onChange={(e) => {
                        handleChangeDirect('chargeIncorrectAnswer', e.target.checked)
                      }}
                      label={t('Charge incorrect answer')}
                    />
                  </FormGroup>
                </Grid>
                {values.chargeIncorrectAnswer &&
                  <Grid item xs={12} md={6}>
                    <ShortFormControl
                      name="priceWithVatIncorrectAnswer"
                      title={t('Price incorrect answer')}
                      value={values.priceWithVatIncorrectAnswer}
                      type="number"
                      handleChange={handleChange}
                    />
                  </Grid>}
              </>}
            </Grid>
          </Paper>
          <Box sx={{ mt: 2, px: 1 }}>
            <Button type={'submit'} variant={'contained'} disabled={user?.readonly}>{t('Save')}</Button>
            {!isNaN(serviceOperatorId) && <>
              <ScheduleChangeDialog getFormData={() => prepareFormData()} model={Consts.ScheduleChange.modelServiceOperator} modelId={Number(serviceOperatorId)} onResult={(res) => {
                if (res) {
                  setForceReloadChangesCounter((prev) => prev + 1)
                }
              }} />
            </>
            }
            { !isNaN(serviceOperatorId) && (<Button type={'button'} color={'error'} variant={'contained'} sx={{ ml: 2 }} disabled={user?.readonly} onClick={() => { setShownDeleteDialog(true) }}>{t('Delete')}</Button>) }
          </Box>
        </Grid>
        <AlertSimpleDialog
          title={t('Delete service operator')}
          message={t('delete-confirm-msg', { name: values.name })}
          open={shownDeleteDialog}
          onClose={handleCloseDeleteDialog}
        />
        {!isNaN(serviceOperatorId) && <Grid item mt={4} xs={12}>
          <ScheduledChanges
              serviceOperatorId={serviceOperatorId}
              setForceReloadChangesCounter={setForceReloadChangesCounter}
              forceReloadChangesCounter={forceReloadChangesCounter}
              t={t}
          />
        </Grid>}
      </Container>
    </Page>
  )
}